import {
  GaEventItems,
  OrderAttributesResponse,
  OrderItemPrice,
  OrderItemPriceAvailabilityTotals,
  OrderItemsResponse
} from 'services/cart/types';
import {
  QOCartResponse,
  PriceAvailabilityLegacyItem,
  QOCookieData
} from 'services/priceAvailabilityLegacy/types';
import { OrderItem } from '@cat-ecom/common/utils/productFormUtils';
import { getValueOrElseEmptyString } from 'utils/string';
import { UpdateOrderItemResponse } from 'services/order/types';
import {
  DEFAULT_BUSINESS_UNIT,
  SHOPPING_PREFERENCES_GA_TRACK_ATTRIBUTES,
  VIEW_CART_EVENT,
  VIEW_ITEM_LIST_EVENT
} from 'constants/analyticsConstants';
import {
  ListData,
  ViewItemListType
} from 'hooks/analytics/use-view-item-list-event';
import {
  PriceData,
  ProductsPriceData,
  SearchAddToCartItemData
} from 'app/[lang]/[esite]/(core)/(search-display)/_components/analytics-listener/types';
import { AddPartToSavedList } from 'services/savedLists/types';
import { normalizedPartNumber } from 'services/cart/utils';
import { ViewItemType } from 'hooks/analytics/use-view-item-event';
import { PAGE_SIZE } from 'app/[lang]/[esite]/(core)/category/[category]/constants';
import {
  DealerCustomerInfo,
  StoreLocation
} from 'services/userPreferences/types';
import { UserDealerSelectionInfo } from 'services/user/types';
import {
  getDefaultLocation,
  getFulfillmentType
} from 'app/[lang]/[esite]/(core)/account/(with-nav)/shopping-preferences/utils';
import { ShoppingPreferencesFormData } from 'app/[lang]/[esite]/(core)/account/(with-nav)/shopping-preferences/types';

type ViewItemsType = {
  product: OrderItemsResponse | PriceData;
  name: string;
  index: number;
  discountAmount: number;
  unitPrice: number;
  manufacturer: string;
  categoryArr: string[];
  locationId?: string;
  availabilityMessage: string;
  quantity?: number | string;
  weight?: number | string;
  serialNumber?: string;
  fitmentCheck?: string;
  itemListData?: ListData;
  productStatus: string;
  relatedModel?: string;
};

export const getAttributeValueForCart = (
  orderItemPriceData: OrderItemPrice[],
  orderItemId: number | string
) => {
  const validPriceData = (orderItemPriceData || []).filter(Boolean);
  const itemData = validPriceData.find(
    (item) => item.orderItemId === orderItemId
  );
  if (!itemData) {
    return {
      availabilityMessage: '',
      discountAmount: 0,
      totalPrice: 0,
      unitPrice: 0
    };
  }
  const {
    availability = [],
    priceWithoutGST: {
      unformatted: { unitPrice = 0.0, discountAmount = 0.0, totalPrice = 0.1 }
    }
  } = itemData;
  return {
    availabilityMessage: availability?.[0]?.availabilityMessage ?? '',
    discountAmount,
    totalPrice,
    unitPrice
  };
};

export const formatPrice = (price: string | number) =>
  typeof price === 'string' ? Number(price.replace(/[$,]/g, '')) : price;

export const getAttributeValueForSearch = (priceData: PriceData) => {
  const {
    availability = [],
    price: { unitPrice = 0.0, discountAmount = 0.0 } = {},
    quantity = 0,
    weight = 0
  } = priceData || {};

  return {
    availabilityMessage:
      availability && availability.length > 0
        ? availability[0]?.availabilityMessage || ''
        : '',
    discountAmount: formatPrice(discountAmount),
    unitPrice: formatPrice(unitPrice),
    quantity,
    weight
  };
};

export const getProductAttr = (productsData: Product[], partNumber: string) => {
  const summaryProductData = productsData?.find(
    (item) => item.partNumber === partNumber.trim()
  );
  const categoryArr = summaryProductData?.categoryPath?.split('/') || [];
  return { manufacturer: summaryProductData?.manufacturer, categoryArr };
};

export const getProductAttrSearch = (productData: ProductsPriceData) => {
  let categoryArr = [];
  if (
    productData?.productPriceData?.categoryPath ||
    productData?.categoryPath
  ) {
    categoryArr =
      productData?.productPriceData?.categoryPath?.split('/') ||
      productData?.categoryPath?.split('/') ||
      [];
  } else if (productData?.categoryPathList) {
    categoryArr = productData?.categoryPathList[0]?.split(':') || [];
  }
  return { manufacturer: productData?.manufacturer, categoryArr };
};

export function getOrderSubTotal(items: PriceAvailabilityLegacyItem[]): number {
  const subTotal = items?.reduce((acc, item) => {
    return acc + item?.price?.unformatted?.unitPrice * item.quantity;
  }, 0);
  return Number(subTotal?.toFixed(2));
}

export const getQOEventData = (
  orderId: string,
  orderItems: QOCartResponse,
  formFields: OrderItem[],
  locationId: string,
  qoCookieData: QOCookieData[]
) => {
  const {
    quickOrderItems: { currency, items },
    products
  } = orderItems;

  const cartDetails = {
    cartId: getValueOrElseEmptyString(orderId)
  };

  const clonedFields = [...formFields];
  clonedFields.reverse();
  const filterPartNumbers = clonedFields?.map((item: OrderItem) => {
    return {
      partNumber: item?.itemNumber,
      quantity: item?.quantity
    };
  });

  const itemsData = items || filterPartNumbers;

  const orderSubTotal = getOrderSubTotal(items);

  const cartItems = (itemsData as PriceAvailabilityLegacyItem[])?.map(
    (partItem, index) => {
      const { manufacturer = '', categoryArr = [] } = getProductAttr(
        products,
        partItem?.partNumber
      );
      const itemListName = qoCookieData?.find(
        (i) =>
          normalizedPartNumber(i.itemNumber) ===
          normalizedPartNumber(partItem.partNumber)
      )?.page;
      return {
        item_id: partItem?.partNumber,
        item_name: `${partItem?.partNumber}: ${partItem?.partName}`,
        affiliation: undefined,
        coupon: undefined,
        discount: partItem?.price?.unformatted?.discountAmount,
        index: index + 1,
        item_brand: manufacturer || undefined,
        item_category: categoryArr?.[0] || undefined,
        item_category2: categoryArr?.[1] || undefined,
        item_category3: categoryArr?.[2] || undefined,
        item_category4: categoryArr?.[3] || undefined,
        item_category5: categoryArr?.[4] || undefined,
        item_list_id: undefined,
        item_list_name: itemListName,
        item_variant: undefined,
        location_id: locationId,
        price: partItem?.price?.unformatted?.unitPrice,
        quantity: partItem?.quantity,
        p_inventory_status: getValueOrElseEmptyString(
          partItem?.availability?.[0]?.availabilityMessage
        ),
        p_weight: partItem?.weight,
        p_serial_number: undefined,
        p_kit_id: null, // BE to send data in get cart details story is open
        p_check_fitment: undefined,
        p_attributes_visible: undefined
      };
    }
  );

  return {
    cartDetails,
    currency,
    orderSubTotal,
    cartItems
  };
};

function getQuantity(item) {
  let exhaustiveQuantity = isNaN(item?.quantity) ? undefined : item?.quantity;
  if (!exhaustiveQuantity) {
    exhaustiveQuantity = item?.productPriceData?.quantity;
  }
  return exhaustiveQuantity;
}

function getDiscount(item) {
  let discount =
    item?.orderItemPrices?.[0]?.priceWithoutGST?.unformatted?.discountAmount;
  if (!discount) {
    discount = item?.productPriceData?.price?.discountAmount;
  }
  return discount;
}

function getInventoryStatus(item) {
  let inventoryStatus =
    item?.orderItemPrices?.[0]?.availability[0]?.availabilityMessage;
  if (!inventoryStatus) {
    inventoryStatus =
      item?.productPriceData?.availability[0]?.availabilityMessage;
  }

  return inventoryStatus || '';
}

function getCategories(item): Array<string> {
  if (item?.categoryPath) {
    return item?.categoryPath?.split('/');
  }

  if (item?.categoryPathList?.length > 0) {
    return item?.categoryPathList[0]?.split(':');
  }

  return [];
}

export const getQuantityChangeEventData = (
  locationId: string,
  itemSummary: (Product | Product[]) & SearchAddToCartItemData,
  itemData:
    | UpdateOrderItemResponse
    | SearchAddToCartItemData
    | SearchAddToCartItemData[]
) => {
  function normalizeToArray(data) {
    if (data == null) return [];
    return Array.isArray(data) ? data : [data];
  }

  const itemsData = normalizeToArray(itemData);
  const itemsSummary = normalizeToArray(itemSummary);

  let subTotal = 0;

  const allItemsData = itemsData?.map((item) => {
    const {
      orderId,
      orderItems,
      orderItemPriceAvailabilityTotals: { currency, orderItemPrices },
      partNumber
    } = item;
    const quantity = Number(orderItems?.[0]?.quantity);
    const price = orderItemPrices[0]?.priceWithoutGST?.unformatted?.unitPrice;
    return {
      orderItemPrices,
      quantity,
      partNumber,
      price,
      currency,
      cartDetails: { cartId: orderId }
    };
  });

  const mergedResponse = itemsSummary?.map((summaryItem) => {
    const allItems = allItemsData.find(
      (item) =>
        normalizedPartNumber(item?.partNumber) ===
        normalizedPartNumber(summaryItem?.partNumber)
    );
    const categoryArr = getCategories(summaryItem);

    return allItems
      ? { ...summaryItem, ...allItems, categoryArr }
      : summaryItem;
  });

  let currency = '';
  const items = mergedResponse?.map((item) => {
    currency = item.productPriceData?.currency || '';
    subTotal +=
      item?.price || item?.productPriceData?.price?.unformattedTotalPrice;

    return {
      affiliation: DEFAULT_BUSINESS_UNIT,
      coupon: undefined,
      discount: getDiscount(item),
      index: item?.index + 1,
      item_brand: item?.manufacturer || undefined,
      item_category2: item?.categoryArr?.[1] || undefined,
      item_category3: item?.categoryArr?.[2] || undefined,
      item_category4: item?.categoryArr?.[3] || undefined,
      item_category5: item?.categoryArr?.[4] || undefined,
      item_category: item?.categoryArr?.[0] || undefined,
      item_id: item?.partNumber,
      item_name: item && `${item?.partNumber}: ${item?.name}`,
      item_variant: undefined,
      location_id: locationId,
      p_attributes_visible: undefined,
      p_check_fitment: item?.fitmentCheck || undefined,
      p_inventory_status: getInventoryStatus(item),
      p_kit_id: null,
      p_serial_number: item?.serialNumber,
      p_weight: item?.productPriceData?.weight,
      price:
        item?.price || item?.productPriceData?.price?.unformattedTotalPrice,
      quantity: getQuantity(item),
      ...(!!item.p_related_model && { p_related_model: item.p_related_model })
    };
  });

  return {
    cartDetails: allItemsData?.[0]?.cartDetails,
    currency: allItemsData?.[0]?.currency || currency,
    cartItem: items,
    value: subTotal
  };
};

const productStatusCache = new Map();

const getProductStatus = (
  partNumber: string,
  productStatus: string
): string => {
  if (productStatus && partNumber) {
    productStatusCache.set(partNumber, productStatus);
  }

  return productStatusCache.get(partNumber) || productStatus || '';
};

export const getProductsViewItems = ({
  product,
  name,
  index,
  discountAmount,
  unitPrice,
  manufacturer,
  categoryArr,
  locationId,
  availabilityMessage,
  quantity,
  weight,
  serialNumber,
  fitmentCheck,
  itemListData,
  productStatus,
  relatedModel
}: ViewItemsType) => {
  // Use the helper function instead of inline logic
  const finalProductStatus = getProductStatus(
    product.partNumber,
    productStatus
  );

  const getCategory = (categoryArr: string[], index: number) =>
    categoryArr?.[index] ?? undefined;

  const getPrice = (
    unitPrice: number,
    product: OrderItemsResponse | PriceData
  ) => unitPrice || product?.unformattedUnitPrice || 0;

  const getQuantity = (
    quantity: number | string,
    product: OrderItemsResponse | PriceData
  ) => {
    const parsedQuantity = Number(quantity);
    if (!isNaN(parsedQuantity) && parsedQuantity > 0) {
      return parsedQuantity;
    }
    const parsedProductQuantity = Number(product?.quantity);
    if (!isNaN(parsedProductQuantity) && parsedProductQuantity > 0) {
      return parsedProductQuantity;
    }
    return 1;
  };

  const getAvailabilityMessage = (
    availabilityMessage: string,
    product: OrderItemsResponse | PriceData
  ) =>
    availabilityMessage ||
    product?.availability?.[0]?.availabilityMessage ||
    undefined;

  const getWeight = (
    weight: number | string,
    product: OrderItemsResponse | PriceData
  ) => weight?.toString() || product?.weight?.toString() || undefined;

  const items = {
    item_id: product.partNumber,
    item_name: `${product.partNumber}: ${name}`,
    affiliation: 'PCC',
    coupon: undefined,
    discount: discountAmount ?? 0,
    index: index + 1,
    item_brand: manufacturer ?? undefined,
    item_category: getCategory(categoryArr, 0),
    item_category2: getCategory(categoryArr, 1),
    item_category3: getCategory(categoryArr, 2),
    item_category4: getCategory(categoryArr, 3),
    item_category5: getCategory(categoryArr, 4),
    item_list_id: itemListData?.item_list_id ?? undefined,
    item_list_name: itemListData?.item_list_name ?? undefined,
    item_variant: undefined,
    location_id: locationId ?? undefined,
    price: getPrice(unitPrice, product),
    quantity: getQuantity(quantity, product),
    p_inventory_status: getAvailabilityMessage(availabilityMessage, product),
    p_weight: getWeight(weight, product),
    p_serial_number: serialNumber ?? undefined,
    ...(!!relatedModel && { p_related_model: relatedModel }),
    p_kit_id: 0,
    p_check_fitment: fitmentCheck ?? undefined,
    p_attributes_visible: undefined,
    p_pim_product_status: finalProductStatus ?? undefined
  };
  return items;
};

export const getDataForOrderItems = (
  orderItems: OrderItemsResponse[],
  orderItemPriceAvailabilityTotals: OrderItemPriceAvailabilityTotals,
  products: Product[],
  locationId: string,
  orderAttributes: OrderAttributesResponse,
  productStatus: string
) => {
  const items = orderItems?.map((product, index) => {
    const { manufacturer, categoryArr } = getProductAttr(
      products,
      product.partNumber
    );

    const { availabilityMessage, discountAmount, unitPrice } =
      getAttributeValueForCart(
        orderItemPriceAvailabilityTotals?.orderItemPrices || [],
        product?.orderItemId
      );

    const params = {
      name: product.description,
      product,
      index,
      discountAmount,
      unitPrice,
      manufacturer,
      categoryArr,
      locationId,
      availabilityMessage,
      quantity: product.quantity,
      weight: product.weight,
      serialNumber: orderAttributes?.equipment?.serialNumber,
      productStatus
    };

    const item: GaEventItems = getProductsViewItems(params);
    return item;
  });

  return items;
};

export const getDataForSearchProducts = (
  products: ProductsPriceData[] | PriceData[],
  itemListData: ListData,
  locationId: string,
  productStatus: string,
  page = 1,
  pageSize?: number
) => {
  const pageLimit = pageSize ?? PAGE_SIZE;
  const indexPage = pageLimit * (page - 1);
  const items = products?.map((product, index) => {
    const { manufacturer, categoryArr } = getProductAttrSearch(product);
    const productIndex =
      product?.index !== undefined ? product?.index : indexPage + index;
    const { availabilityMessage, discountAmount, unitPrice, quantity, weight } =
      getAttributeValueForSearch(product.productPriceData);

    const item: GaEventItems = getProductsViewItems({
      name: product.name,
      product,
      index: productIndex,
      discountAmount,
      unitPrice,
      manufacturer,
      categoryArr,
      locationId,
      availabilityMessage,
      quantity,
      weight,
      serialNumber: product.serialNumber,
      ...(product.p_related_model && { relatedModel: product.p_related_model }),
      fitmentCheck: product.fitmentCheck,
      itemListData,
      productStatus
    });

    return item;
  });

  return items;
};

export const getViewItems = ({
  cartId,
  cartItemsData,
  products,
  locationId,
  type,
  isProductsData = false,
  itemListData,
  productStatus,
  page,
  pageSize,
  isDiagramTab = false
}: ViewItemListType) => {
  type Ecommerce = {
    items: GaEventItems[];
    item_list_id?: string;
    item_list_name?: string;
    currency?: unknown;
    value?: number | string;
  };

  const cartDetails = {
    cartID: cartId || undefined
  };

  const { orderItemPriceAvailabilityTotals, orderItems, orderAttributes } =
    cartItemsData || {};

  const items = !isProductsData
    ? getDataForOrderItems(
        orderItems,
        orderItemPriceAvailabilityTotals,
        products,
        locationId,
        orderAttributes,
        productStatus || ''
      )
    : getDataForSearchProducts(
        products,
        itemListData,
        locationId,
        productStatus || '',
        page,
        pageSize
      );

  const ecommerce: Ecommerce = {
    items: items
  };

  if (isDiagramTab) {
    ecommerce.currency = '';
  }

  if (type === VIEW_CART_EVENT) {
    ecommerce.currency = getValueOrElseEmptyString(
      orderItemPriceAvailabilityTotals?.currency || ''
    );
    ecommerce.value =
      orderItemPriceAvailabilityTotals?.orderTotals?.unformatted
        ?.orderSubTotalWithGST || '';
  } else {
    ecommerce.currency = getValueOrElseEmptyString(
      orderItemPriceAvailabilityTotals?.currency || ''
    );
    ecommerce.item_list_id = itemListData?.item_list_id ?? undefined;
    ecommerce.item_list_name = itemListData?.item_list_name ?? undefined;
    ecommerce.currency = itemListData?.currency ?? undefined;
  }

  return {
    event:
      type === VIEW_ITEM_LIST_EVENT ? VIEW_ITEM_LIST_EVENT : VIEW_CART_EVENT,
    cartDetails,
    ecommerce
  };
};

export const getViewItem = ({
  cartId,
  products,
  locationId,
  type,
  itemListData,
  productStatus,
  page
}: ViewItemListType) => {
  type Ecommerce = {
    items: GaEventItems[];
    item_list_id?: string;
    item_list_name?: string;
    currency?: unknown;
    value?: number | string;
  };
  const cartDetails = {
    cartID: cartId || undefined
  };
  const items = getDataForSearchProducts(
    products,
    itemListData,
    locationId,
    productStatus,
    page
  );
  const [
    {
      productPriceData: {
        currency = '',
        price: { unitPrice }
      }
    }
  ] = products;
  const ecommerce: Ecommerce = {
    items,
    currency,
    value: formatPrice(unitPrice ?? 0.0)
  };
  return {
    event: type,
    cartDetails,
    ecommerce
  };
};

export const getAddToWishlistEventData = (
  orderId?: string,
  currencyCode?: string,
  wishlistItems?: AddPartToSavedList[],
  locationId?: string,
  products?: Product[],
  items?: PriceAvailabilityLegacyItem[]
) => {
  const currency = currencyCode;
  const cartDetails = {
    cartId: getValueOrElseEmptyString(orderId)
  };

  const filterPartNumbers = wishlistItems?.map((item: AddPartToSavedList) => {
    return {
      partNumber: item?.partNumber,
      partName: item?.name,
      quantity: item?.quantity,
      price: 0
    };
  });

  const itemsData = items?.length > 0 ? items : filterPartNumbers;
  const orderSubTotal = items?.length > 0 ? getOrderSubTotal(items) : 0;
  const ItemData = itemsData?.map((partItem, index) => {
    const { manufacturer = '', categoryArr = [] } = getProductAttr(
      products,
      partItem?.partNumber
    );
    return {
      item_id: partItem?.partNumber,
      item_name: `${partItem?.partNumber}: ${partItem?.partName}`,
      affiliation: '',
      coupon: '',
      discount: partItem?.price?.unformatted?.discountAmount || 0,
      index: index + 1,
      item_brand: manufacturer || '',
      item_category: categoryArr?.[0] || '',
      item_category2: categoryArr?.[1] || '',
      item_category3: categoryArr?.[2] || '',
      item_category4: partItem?.partNumber || '',
      item_category5: categoryArr?.[3] || '',
      item_list_id: '',
      item_list_name: '',
      item_variant: '',
      location_id: locationId,
      price: partItem?.price?.unformatted?.discountedPrice || 0,
      quantity: partItem?.quantity,
      p_inventory_status: '',
      p_weight: partItem?.weight || '',
      p_serial_number: '',
      p_kit_id: '',
      p_check_fitment: '',
      p_attributes_visible: '',
      p_pim_product_status: ''
    };
  });

  return {
    cartDetails,
    currency,
    orderSubTotal,
    ItemData
  };
};

export const getViewItemEventData = ({
  cartId,
  products,
  locationId,
  type,
  itemListData,
  priceData,
  productStatus
}: ViewItemType) => {
  type Ecommerce = {
    items: GaEventItems[];
    item_list_id?: string;
    item_list_name?: string;
    value?: number;
    currency?: unknown;
  };
  const cartDetails = {
    cartID: cartId || undefined
  };

  const items = getDataForSearchProducts(
    products,
    itemListData,
    locationId,
    productStatus
  );

  const currencyCode = priceData?.currency;
  const rawPrice = priceData ? priceData?.items[0]?.unitPrice : '0';
  const unitPrice = parseFloat(rawPrice?.replace(/[^0-9.]/g, ''));

  const ecommerce: Ecommerce = {
    items: items,
    value: unitPrice,
    currency: currencyCode
  };

  return {
    event: type,
    cartDetails,
    ecommerce
  };
};

export const buildEcommerceEvent = () => {
  return {
    ecommerce: null
  };
};

const changeFieldVal = (fieldValue: boolean) => {
  const value = fieldValue
    ? SHOPPING_PREFERENCES_GA_TRACK_ATTRIBUTES.YES
    : SHOPPING_PREFERENCES_GA_TRACK_ATTRIBUTES.NO;
  return value;
};

export const generateFormContent = (
  data: ShoppingPreferencesFormData,
  languagePreferences: StoreLocation[]
) => {
  const {
    isQuoteEmailPreference,
    isDisplayOrderInformation,
    isDisplayLineItemNotes,
    isQuickOrderEnabled,
    showAvailability,
    fulfillment,
    isSkipPickupAndDeliveryPage,
    purchaseOrderNumberPreference: { isPurchaseOrderNumberDisplayed = false },
    startPage,
    languageId
  } = data;

  const language =
    getDefaultLocation(languagePreferences, languageId)?.displayName ?? '';
  const fulfillmentType = getFulfillmentType(fulfillment);

  return {
    formContent: `order details : attach quote pdf- ${changeFieldVal(isQuoteEmailPreference)}, editingorderInfo-${changeFieldVal(isDisplayOrderInformation)} | shopping cart : notestocart : ${changeFieldVal(isDisplayLineItemNotes)},quickorderoncart: ${changeFieldVal(isQuickOrderEnabled)},availability : ${showAvailability === 'S' ? 'show availability by source' : 'show estimated dates'}`,
    formContent2: `skip p & d page - ${changeFieldVal(isSkipPickupAndDeliveryPage)} , Fulfilment : ${fulfillmentType}| view: showimages - ${changeFieldVal(isDisplayOrderInformation)}`,
    formAdditionalContent: `Display purchase order Number : ${changeFieldVal(isPurchaseOrderNumberDisplayed)} | Login settings : start page: ${startPage}, language settings: ${language ?? SHOPPING_PREFERENCES_GA_TRACK_ATTRIBUTES.NA}`
  };
};

export const generateCSPFormContent = (
  dealerCustomerInfo: DealerCustomerInfo,
  userDealerSelection: Pick<UserDealerSelectionInfo, 'storeLocationName'>
) => {
  const { customerNumber, customerName } = dealerCustomerInfo;
  const { storeLocationName } = userDealerSelection;

  return {
    formContent: `Dealer Account Number  : ${customerNumber}-${customerName} | Dealer Store : ${storeLocationName}`
  };
};
